import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Send, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { parseOffersFromText } from "@/lib/api";

const fmtMoney = (n) => "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0 });

export default function AIOfferInput({ analysisId, onOffersCreated, requireAuth }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleParse = async () => {
    if (!text.trim()) { toast.error("Describe your loan offers"); return; }
    setLoading(true);
    setResult(null);
    try {
      const res = await parseOffersFromText(text, analysisId);
      if (res.data.error) {
        toast.error(res.data.error);
      } else {
        setResult(res.data);
        toast.success(`AI parsed ${res.data.count} loan offers`);
        onOffersCreated?.();
      }
    } catch (err) {
      toast.error("Failed to parse offers");
    }
    setLoading(false);
  };

  const handleClose = () => {
    setOpen(false);
    setText("");
    setResult(null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => {
      if (v && requireAuth && !requireAuth()) return;
      if (!v) handleClose(); else setOpen(true);
    }}>
      <DialogTrigger asChild>
        <Button
          data-testid="ai-offer-btn"
          className="bg-[#1A362D] hover:bg-[#264B40] gap-2"
        >
          <Sparkles className="w-4 h-4 text-[#E3B505]" />
          AI Offer Input
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Playfair_Display'] flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#E3B505]" />
            AI Loan Offer Input
          </DialogTitle>
          <DialogDescription>
            Describe your loan offers in plain English. The AI will parse and organize them automatically.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4 pt-2">
            <Textarea
              data-testid="ai-offer-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={`Example:\nI got an offer from BlueVine for $150k, 24 months, weekly payments, factor rate 1.35 with a $3k origination fee. They need a personal guarantee.\n\nAlso got one from OnDeck - $100k short term loan, 12 months daily payments, factor rate 1.28, $2500 origination, $1500 closing costs. Prepayment penalty of 25% remaining balance.\n\nAnd an SBA loan from my bank for $250k, 5 year term, 10.5% interest, monthly payments. $5k origination, $3k closing. Needs collateral and PG.`}
              className="min-h-[200px] text-sm"
            />
            <Button
              data-testid="ai-offer-submit"
              onClick={handleParse}
              disabled={loading || !text.trim()}
              className="w-full bg-[#1A362D] hover:bg-[#264B40]"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  AI is parsing your offers...
                </div>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Parse Offers with AI</>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <div className="flex items-center gap-2 p-3 bg-[#2D4A3E]/10 rounded-md">
              <Check className="w-5 h-5 text-[#2D4A3E]" />
              <span className="text-sm font-medium text-[#2D4A3E]">
                Successfully created {result.count} loan offer{result.count !== 1 ? "s" : ""}
              </span>
            </div>

            {result.offers?.map((offer, i) => (
              <div key={i} className="border border-[#E7E5DF] rounded-md p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-['Playfair_Display'] font-semibold text-[#1C1917]">
                    {offer.lender_name}
                  </h4>
                  <span className="text-xs px-2 py-0.5 rounded bg-[#F1EFEA] text-[#57534E]">
                    {offer.product_type}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <p className="text-[#78716C] text-xs">Loan Amount</p>
                    <p className="font-['IBM_Plex_Mono'] font-semibold">{fmtMoney(offer.loan_amount)}</p>
                  </div>
                  <div>
                    <p className="text-[#78716C] text-xs">Monthly Payment</p>
                    <p className="font-['IBM_Plex_Mono'] font-semibold">{fmtMoney(offer.monthly_payment)}</p>
                  </div>
                  <div>
                    <p className="text-[#78716C] text-xs">Total Cost</p>
                    <p className="font-['IBM_Plex_Mono'] font-semibold text-[#E07A5F]">{fmtMoney(offer.total_financing_cost)}</p>
                  </div>
                </div>
              </div>
            ))}

            <Button
              data-testid="ai-offer-done"
              onClick={handleClose}
              className="w-full bg-[#1A362D] hover:bg-[#264B40]"
            >
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
